import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { formValueSelector } from 'redux-form';

// Utils
import importImages from '../../../../util/webpack';
import { formatWeddingDate, formatLocation } from '../util/previewUtils';
import featureFlag from  '../../../../util/featureFlags';

// Components
import WeddingParty from '../../../PublicWebsite/pages/WeddingParty';
import PoiV2 from '../../../PublicWebsite/pages/PoiV2';
import Faq from '../../../PublicWebsite/pages/Faq';
import Registry from '../../../PublicWebsite/pages/Registry';
import Photos from '../../../PublicWebsite/pages/Photos';
import Travel from '../../../PublicWebsite/pages/Travel';
import Events from '../../../PublicWebsite/pages/Events';
import Home from '../../../PublicWebsite/pages/Home';
import Rsvp from '../../../PublicWebsite/pages/Rsvp';
import PublicPagesNav from '../../../PublicWebsite/PublicPagesNav';
import PublicHeader from '../../../PublicWebsite/PublicHeader';
import PublicFooter from '../../../PublicWebsite/PublicFooter';

// Actions
import { showModal } from '../../../../actions/ModalActions';

const isCmsPageFormV2 = featureFlag.get('isCmsPageFormV2');
const previewImages = importImages(require.context('../../../../assets/images/icons/', false, /\.(jpg|jpeg|png|gif|svg)(\?.*)?$/));

const PAGE_COMPONENTS = {
  WEDDING_PARTY: WeddingParty,
  POI: PoiV2,
  FAQ: Faq,
  REGISTRY: Registry,
  PHOTO: Photos,
  TRAVEL: Travel,
  HOME: Home,
  EVENT: Events,
  RSVP: Rsvp
};

const WebsitePreview = ({
  activePage,
  isHome,
  values,
  pages,
  wedding,
  account,
  weddingValues,
}) => {
  const CurrentPage = activePage ? PAGE_COMPONENTS[activePage.type] : null;
  const pageData = Object.assign({}, activePage, values);
  const date = isHome ? weddingValues.date : wedding.wedding_date;
  const weddingDate = formatWeddingDate(date);

  // data that uses live form values
  const formData = {
    title: isHome ? weddingValues.title : wedding.title,
    location: isHome ? formatLocation(weddingValues.city, weddingValues.stateProvince) : formatLocation(account.city, account.state_province),
    hashtag: isHome ? weddingValues.hashtag : wedding.hashtag,
    weddingDate,
    city: isHome ? weddingValues.city : null,
    stateProvince: isHome ? weddingValues.stateProvince : null,
  };
  const liveNavPages = activePage
    ? pages.map((page) => {
      if (page.type !== activePage.type) return page;
      const title = isHome ? values.nav_title : values.title;
      return { ...page, nav_title: title };
    })
    : null;

  return (
    <div className="website-preview-v2 margin-top">
      <div className="preview-container" id="public-wrapper">
        <div className="chrome-img" />
        {activePage && activePage.hidden && (
          <div id="page-hidden-banner" className="text-center">
            <p><img src={previewImages('npHidden')} className="margin-right-2x" alt="Not Visible Icon" />This page is hidden, visible only to you.</p>
          </div>
        )}
        <div className="preview-frame-container">
          <div className="preview-frame">
            <div className="sticky-nav">
              {wedding.slug && formData.title && (
                <PublicHeader
                  slug={wedding.slug}
                  name={formData.title}
                  weddingDate={weddingDate}
                  location={formData.location}
                  hashtag={formData.hashtag}
                />
              )}
              {wedding.slug && pages && (
                <PublicPagesNav slug={wedding.slug} pages={liveNavPages} />
              )}
            </div>
            <div id="public-page-content-wrapper">
              {CurrentPage && <CurrentPage forPreview page={pageData} formData={formData} />}
              {wedding.owner_first_name && wedding.partner_first_name && activePage && activePage.type !== 'HOME' && account.enable_seal && (
                <div className="container seal">
                  <div className="seal-section">
                    <span className="name-1">
                      {wedding.owner_first_name && (
                        <span className="initial-1">{wedding.owner_first_name[0]}</span>
                      )}
                      {wedding.owner_last_name && (
                        <span className="initial-2">{wedding.owner_last_name[0]}</span>
                      )}
                    </span>
                    {wedding.owner_first_name && wedding.partner_first_name && (
                      <span>
                        <span className="amp">&amp;</span>
                        <span className="and">and</span>
                        <span className="plus">+</span>
                      </span>
                    )}
                    <span className="name-2">
                      {wedding.partner_first_name && (
                        <span className="initial-1">{wedding.partner_first_name[0]}</span>
                      )}
                      {wedding.partner_last_name && (
                        <span className="initial-2">{wedding.partner_last_name[0]}</span>
                      )}
                    </span>
                  </div>
                </div>
              )}
            </div>
            {activePage && (
              <PublicFooter page={activePage} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

WebsitePreview.propTypes = {
  values: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    hidden: PropTypes.bool,
  }),
  activePage: PropTypes.shape({}),
  pages: PropTypes.arrayOf(PropTypes.shape({})),
  wedding: PropTypes.shape({}),
  account: PropTypes.shape({}),
};

const getFormIdentifier = (pageType) => {
  return (pageType && `${pageType.toLowerCase()}EditPageForm` || 'EditPageForm');
}

const mapStateToProps = (state) => {
  const page = state.website.pages.byId[state.website.pages.activePage.id];
  const pageType = page && page.type;
  const cmsPageFormIdentifier = isCmsPageFormV2 ? getFormIdentifier(pageType) : 'CmsPageForm';
  const cmsPageFormSelector = formValueSelector(cmsPageFormIdentifier);
  const homePageFormSelector = formValueSelector('homepageSection');
  const weddingInfoFormSelector = formValueSelector('weddingInfoForm');
  const isHome = page && page.type === 'HOME';
  const { date, wedding, account } = weddingInfoFormSelector(state, 'date', 'hashtag', 'wedding', 'account');
  return {
    isHome,
    values: isHome
      ? homePageFormSelector(state, 'title', 'header_image_url', 'footer_image_url', 'nav_title')
      : cmsPageFormSelector(state, 'title', 'description', 'hidden', 'header_image_url'),
    activePage: page,
    pages: state.website.pages.orderedIds.map(id => state.website.pages.byId[id]),
    wedding: state.wedding.wedding,
    weddingValues: isHome
      ? {
        date,
        hashtag: wedding.hashtag,
        title: wedding.title,
        city: account.city,
        stateProvince: account.state_province,
      }
      : null,
    account: state.wedding.account,
    entities: state.website.entities,
    guests: state.guestList.guests
  };
};

const mapDispatchToProps = dispatch => ({
  openEventFormModal: () => dispatch(showModal('EVENT_FORM', { collectRsvps: true, websiteEvent: true, formIdentifier: 'eventForm' }, { size: 'md' })),
});

const WebsitePreviewContainer = connect(mapStateToProps, mapDispatchToProps)(WebsitePreview);

export default WebsitePreviewContainer;



// WEBPACK FOOTER //
// ./src/components/manage/website/WebsitePreview/WebsitePreview.jsx